import type { EmployeeTimeEntry } from "@/lib/employeeTimes";

export type PeriodPreset = "month" | "prev_month" | "year" | "all" | "custom";
export type PaidFilter = "open" | "paid" | "all";

export interface DateRange {
  from: string | null;
  to: string | null;
}

export const PERIOD_OPTIONS: Array<{ value: PeriodPreset; label: string }> = [
  { value: "month", label: "Dieser Monat" },
  { value: "prev_month", label: "Letzter Monat" },
  { value: "year", label: "Dieses Jahr" },
  { value: "all", label: "Alle" },
  { value: "custom", label: "Zeitraum" },
];

function toIso(year: number, monthIndex: number, day: number): string {
  return `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function lastDayOfMonth(year: number, monthIndex: number): number {
  return new Date(year, monthIndex + 1, 0).getDate();
}

/** Datumsgrenzen für eine Voreinstellung; bei „custom“ bleiben die eigenen Werte. */
export function resolvePeriod(
  preset: PeriodPreset,
  custom: DateRange,
  today: Date = new Date(),
): DateRange {
  const y = today.getFullYear();
  const m = today.getMonth();
  switch (preset) {
    case "month":
      return { from: toIso(y, m, 1), to: toIso(y, m, lastDayOfMonth(y, m)) };
    case "prev_month": {
      const py = m === 0 ? y - 1 : y;
      const pm = m === 0 ? 11 : m - 1;
      return { from: toIso(py, pm, 1), to: toIso(py, pm, lastDayOfMonth(py, pm)) };
    }
    case "year":
      return { from: toIso(y, 0, 1), to: toIso(y, 11, 31) };
    case "all":
      return { from: null, to: null };
    case "custom":
      return { from: custom.from, to: custom.to };
  }
}

export function formatDateDe(iso: string | null | undefined): string {
  if (!iso) return "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  if (!y || !m || !d) return iso;
  return `${d}.${m}.${y}`;
}

export function describeRange(range: DateRange): string {
  if (!range.from && !range.to) return "Alle Zeiträume";
  if (range.from && range.to) return `${formatDateDe(range.from)} – ${formatDateDe(range.to)}`;
  if (range.from) return `ab ${formatDateDe(range.from)}`;
  return `bis ${formatDateDe(range.to)}`;
}

export function projectLabel(entry: EmployeeTimeEntry): string {
  const nr = entry.auftragsnummer ? String(entry.auftragsnummer) : null;
  const kunde = entry.kunde?.trim() || null;
  if (nr && kunde) return `${nr} · ${kunde}`;
  if (nr) return nr;
  if (kunde) return kunde;
  return entry.beschreibung?.trim() || "—";
}

export function isPaid(entry: EmployeeTimeEntry): boolean {
  return Boolean(entry.subcontractor_paid_at);
}

export function matchesPaidFilter(entry: EmployeeTimeEntry, filter: PaidFilter): boolean {
  if (filter === "all") return true;
  return filter === "paid" ? isPaid(entry) : !isPaid(entry);
}

function normalize(value: string | null | undefined): string {
  return String(value ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function matchesSearch(entry: EmployeeTimeEntry, query: string): boolean {
  const q = normalize(query).trim();
  if (!q) return true;
  const haystack = normalize(
    [
      entry.user_display,
      entry.user_login,
      projectLabel(entry),
      entry.beschreibung,
      entry.ort,
      entry.comment,
      entry.subcontractor_invoice_number,
    ]
      .filter(Boolean)
      .join(" "),
  );
  return q.split(/\s+/).every((token) => haystack.includes(token));
}

export function filterEntries(
  entries: EmployeeTimeEntry[],
  paid: PaidFilter,
  query: string,
): EmployeeTimeEntry[] {
  return entries.filter((e) => matchesPaidFilter(e, paid) && matchesSearch(e, query));
}

export function sumHours(entries: EmployeeTimeEntry[]): number {
  return entries.reduce((sum, e) => sum + Number(e.hours_decimal || 0), 0);
}

export interface EntryGroup {
  key: string;
  label: string;
  entries: EmployeeTimeEntry[];
  hours: number;
  openEntries: EmployeeTimeEntry[];
  openHours: number;
}

/** Gruppiert je Nachunternehmer, alphabetisch; innerhalb bleibt die Reihenfolge erhalten. */
export function groupByEmployee(entries: EmployeeTimeEntry[]): EntryGroup[] {
  const map = new Map<string, EntryGroup>();
  for (const entry of entries) {
    const key = (entry.user_login || "").toLowerCase() || entry.user_display || "—";
    let group = map.get(key);
    if (!group) {
      group = {
        key,
        label: entry.user_display?.trim() || entry.user_login || "—",
        entries: [],
        hours: 0,
        openEntries: [],
        openHours: 0,
      };
      map.set(key, group);
    }
    group.entries.push(entry);
  }
  const groups = [...map.values()];
  for (const group of groups) {
    group.hours = sumHours(group.entries);
    group.openEntries = group.entries.filter((e) => !isPaid(e));
    group.openHours = sumHours(group.openEntries);
  }
  return groups.sort((a, b) => a.label.localeCompare(b.label, "de"));
}
